import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, of, defer } from 'rxjs';
import {
	catchError,
	concatMap,
	debounceTime,
	finalize,
	map,
	takeUntil,
	timeout,
} from 'rxjs/operators';
import {
	SimulatePaymentRequest,
	SimulatePaymentResponse,
} from 'src/app/models/apis/pre-offer';
import { OnboardingService } from 'src/app/services/apis/onboarding.service';
import { AnalyticalLogsService } from 'src/app/services/utils/analytical-logs.service';
import { PreApplicationActions } from 'src/app/state/actions/pre-application.actions';

@Component({
	selector: 'cmv-credit-simulator',
	templateUrl: './credit-simulator.component.html',
	styleUrls: ['./credit-simulator.component.scss'],
})
export class CreditSimulatorComponent implements OnInit, OnDestroy {
	@Input() pagoMensual: number = 200;
	@Input() plazoLocal: number = 36;
	@Input() montoLocal: number = 80000;
	@Input() authorizedAmount: number = 80000;
	/** Monto mínimo que se puede solicitar del producto. */
	@Input() minimumAmount: number = 5000;
	/** Plazo máximo autorizado; los plazos mayores no se ofrecen. 0 = todos. */
	@Input() maxTerm: number = 0;
	@Input() tasa: number = 97.08;
	@Input() promoBox: boolean = false;
	@Input() reimbursementType: 'FRANCESA' | 'ALEMANA' = 'FRANCESA';
	@Output() paymentLoadingEvent: EventEmitter<boolean> = new EventEmitter();
	cargandoPago: boolean = false;
	simulationError: boolean = false;

	private simulate$ = new Subject<{ monto: number; plazo: number }>();
	private destroy$ = new Subject<void>();

	constructor(
		private _apiOnboarding: OnboardingService,
		private analyticalLogService: AnalyticalLogsService,
		private store: Store
	) {}

	ngOnInit(): void {
		if (sessionStorage.getItem('datosParametrizacion')) {
			const datosParam = JSON.parse(
				sessionStorage.getItem('datosParametrizacion')
			);
			// El monto guardado puede venir de otro producto con un rango distinto.
			this.montoLocal = this.clampAmount(
				datosParam.Monto > 0 ? datosParam.Monto : this.authorizedAmount
			);
			// El plazo guardado puede ser mayor al autorizado para este socio; el
			// selector termina de ajustarlo al plazo permitido más cercano.
			this.plazoLocal =
				this.maxTerm > 0 && datosParam.Plazo > this.maxTerm
					? this.maxTerm
					: datosParam.Plazo;
		} else {
			this.montoLocal = this.authorizedAmount;
		}

		this.simulate$
			.pipe(
				debounceTime(300),
				// concatMap procesa cada cambio en orden SIN cancelar la petición
				// anterior; así se evita que peticiones canceladas dejen conexiones
				// colgadas (con XHR instrumentado por DataDog la cancelación no
				// siempre libera la conexión y se agota el pool). El defer() hace que
				// cualquier throw síncrono sea un error del observable interno
				// (capturado por catchError) y no mate el Subject.
				concatMap(({ monto, plazo }) => {
					const safeMonto = monto > 0 ? monto : this.minimumAmount;
					return defer(() => {
						this.startPaymentLoading();
						this.simulationError = false;
						const datos: SimulatePaymentRequest = {
							Monto: safeMonto,
							Plazo: plazo,
							Tasa: this.tasa,
							FechaPtmo: new Date().toISOString().split('T')[0],
							reimbursement_type: this.reimbursementType,
						};
						sessionStorage.setItem(
							'datosParametrizacion',
							JSON.stringify(datos)
						);
						return this._apiOnboarding.simulatePayment(datos);
					}).pipe(

						timeout(15000),
						map((response: SimulatePaymentResponse) => ({
							success: true,
							response,
							monto: safeMonto,
							plazo,
						})),
						catchError((err: Error) => {
							console.error(err);
							this.analyticalLogService.logAnalytics({
								exception_error: err.message,
							});
							return of({
								success: false,
								response: null,
								monto: safeMonto,
								plazo,
							});
						}),
						finalize(() => this.stopPaymentLoading())
					);
				}),
				takeUntil(this.destroy$)
			)
			.subscribe({
				next: ({ success, response, monto, plazo }) => {
					if (success && response) {
						const { total, cat } = response;
						this.pagoMensual = total;
						this.simulationError = false;
						this.store.dispatch(
							PreApplicationActions.addDataFromSimulator({
								amount: monto,
								creditTerm: plazo,
								subProductId: 245,
								periodPayment: total,
								rate: this.tasa,
								periodicityId: 1,
								cat,
							})
						);
					} else {
						this.simulationError = true;
					}
				},
			});

		this.calculaPago(this.montoLocal, this.plazoLocal);
	}

	calculaPago(monto: number, plazo: number): void {
		this.montoLocal = monto || this.montoLocal;
		this.plazoLocal = plazo || this.plazoLocal;
		this.simulate$.next({ monto: this.montoLocal, plazo: this.plazoLocal });
	}

	private clampAmount(amount: number): number {
		if (amount < this.minimumAmount) {
			return this.minimumAmount;
		}
		if (this.authorizedAmount > 0 && amount > this.authorizedAmount) {
			return this.authorizedAmount;
		}
		return amount;
	}

	private stopPaymentLoading(): void {
		this.cargandoPago = false;
		this.paymentLoadingEvent.emit(this.cargandoPago);
	}

	private startPaymentLoading(): void {
		this.cargandoPago = true;
		this.paymentLoadingEvent.emit(this.cargandoPago);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
